import { useState, useEffect, useCallback } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { useHistory } from 'react-router-dom';
import { convertToRaw } from 'draft-js';
import draftToHtml from 'draftjs-to-html';
import { createEditorStateFromHTML } from '../utils/editorUtils';
import {
  getPromotion,
  getCitiesList,
  getInitiatorsList,
  getButtonsList,
  createPromotion,
  updatePromotion,
  createInitiatorAction,
  clearPromotionData,
  clearErrors
} from '../../../store/companyPromotions';
import {
  selectCurrentPromotion,
  selectPromotionLoading,
  selectCities,
  selectInitiators,
  selectButtons,
  selectCreateLoading,
  selectCreateError,
  selectUpdateLoading,
  selectUpdateError
} from '../selectors';

// Начальное состояние формы
const getInitialFormData = () => ({
  // Название акции
  name_ru: '',
  name_kk: '',
  name_en: '',

  // Краткое описание
  short_description_ru: '',
  short_description_kk: '',
  short_description_en: '',

  // Баннеры
  banner_ru: null,
  banner_kk: null,
  banner_en: null,

  // Полное описание (EditorState)
  full_description_ru: null,
  full_description_kk: null,
  full_description_en: null,

  // Сроки проведения
  date_start: null,
  date_end: null,

  // Настройки кнопок
  show_buttons: true,
  buttons: [
    {
      button_id: null,
      link: '',
      is_external_link: false
    }
  ],

  // Доступность
  cities: [],
  availability_type: 'all',
  file_ids: [],

  // Инициатор
  selected_initiator: null,
  initiator: {
    name: '',
    surname: '',
    department: '',
    phone: ''
  }
});

/**
 * Общий хук для управления формой акции (создание/редактирование/копирование)
 * @param {string} mode - Режим: 'create', 'edit', 'copy'
 * @param {string|null} promotionId - ID акции для редактирования/копирования
 * @returns {Object} - Объект с данными и функциями для управления формой
 */
export const usePromotionForm = (mode = 'create', promotionId = null) => {
  const dispatch = useDispatch();
  const history = useHistory();

  const currentPromotion = useSelector(selectCurrentPromotion);
  const promotionLoading = useSelector(selectPromotionLoading);
  const cities = useSelector(selectCities);
  const initiators = useSelector(selectInitiators);
  const buttons = useSelector(selectButtons);
  const createLoading = useSelector(selectCreateLoading);
  const createError = useSelector(selectCreateError);
  const updateLoading = useSelector(selectUpdateLoading);
  const updateError = useSelector(selectUpdateError);

  const [formData, setFormData] = useState(getInitialFormData());
  const [isInitialized, setIsInitialized] = useState(false);

  // Определяем состояния загрузки и ошибок в зависимости от режима
  const loading = mode === 'create' ? createLoading : updateLoading;
  const error = mode === 'create' ? createError : updateError;

  // Загрузка необходимых данных
  useEffect(() => {
    dispatch(getCitiesList());
    dispatch(getInitiatorsList());
    dispatch(getButtonsList());

    if ((mode === 'edit' || mode === 'copy') && promotionId) {
      dispatch(getPromotion(promotionId));
    }

    return () => {
      dispatch(clearPromotionData());
      dispatch(clearErrors());
    };
  }, [dispatch, mode, promotionId]);

  // Инициализация формы данными акции (для редактирования/копирования)
  useEffect(() => {
    if (currentPromotion && (mode === 'edit' || mode === 'copy') && !isInitialized) {
      const getTranslation = (lang) => {
        return currentPromotion.translations?.find((t) => t.lang === lang) || {};
      };

      // Получение филиалов для cities
      const selectedCities =
        currentPromotion.filial_ids?.length > 0 && cities?.filials
          ? currentPromotion.filial_ids
              .map((id) => {
                const filial = cities.filials.find((f) => f.filial_id === id);
                return filial ? { value: filial.filial_id, label: filial.city_name } : null;
              })
              .filter(Boolean)
          : [];

      // Если выбраны все филиалы или нет выбранных, устанавливаем 'all'
      const isAllFilials =
        !currentPromotion.filial_ids?.length ||
        (cities?.filials && currentPromotion.filial_ids.length === cities.filials.length);

      setFormData({
        name_ru: getTranslation('ru').promotion_name || '',
        name_kk: getTranslation('kk').promotion_name || '',
        name_en: getTranslation('en').promotion_name || '',
        short_description_ru: getTranslation('ru').short_description || '',
        short_description_kk: getTranslation('kk').short_description || '',
        short_description_en: getTranslation('en').short_description || '',
        banner_ru: getTranslation('ru').banner_url || null,
        banner_kk: getTranslation('kk').banner_url || null,
        banner_en: getTranslation('en').banner_url || null,
        full_description_ru: createEditorStateFromHTML(getTranslation('ru').description, 'RU'),
        full_description_kk: createEditorStateFromHTML(getTranslation('kk').description, 'KK'),
        full_description_en: createEditorStateFromHTML(getTranslation('en').description, 'EN'),
        date_start: currentPromotion.start_at ? new Date(currentPromotion.start_at) : null,
        date_end: currentPromotion.end_at ? new Date(currentPromotion.end_at) : null,

        // Настройки кнопок
        show_buttons: currentPromotion.is_button_enabled || false,
        buttons:
          currentPromotion.buttons?.length > 0
            ? currentPromotion.buttons.map((btn) => ({
                button_id: btn.id,
                link: btn.link,
                is_external_link: btn.is_external_link
              }))
            : [
                {
                  button_id: null,
                  link: '',
                  is_external_link: false
                }
              ],

        // Доступность
        cities: isAllFilials ? ['all'] : selectedCities,
        availability_type: currentPromotion.is_enabled_for_all_customers ? 'all' : 'customer_id',
        file_ids: currentPromotion.file_ids || [],

        // Инициатор
        selected_initiator: currentPromotion.initiator_id
          ? { value: currentPromotion.initiator_id }
          : null,
        initiator: {
          name: '',
          surname: '',
          department: '',
          phone: ''
        }
      });

      setIsInitialized(true);
    }
  }, [currentPromotion, mode, cities, isInitialized]);

  // Обновление selected_initiator с полными данными когда загрузятся инициаторы
  useEffect(() => {
    if (
      formData.selected_initiator?.value &&
      initiators?.initiators &&
      !formData.selected_initiator.label
    ) {
      const initiator = initiators.initiators.find(
        (init) => init.id === formData.selected_initiator.value
      );
      if (initiator) {
        setFormData((prev) => ({
          ...prev,
          selected_initiator: {
            value: initiator.id,
            label: `${initiator.name} ${initiator.surname}`
          }
        }));
      }
    }
  }, [initiators, formData.selected_initiator]);

  // Функция для обновления данных формы
  const updateFormData = useCallback((field, value) => {
    setFormData((prev) => ({
      ...prev,
      [field]: value
    }));
  }, []);

  // Функция для обновления вложенных данных
  const updateNestedFormData = useCallback((parentField, field, value) => {
    setFormData((prev) => ({
      ...prev,
      [parentField]: {
        ...prev[parentField],
        [field]: value
      }
    }));
  }, []);

  // Функция отправки формы
  const handleSubmit = useCallback(
    async (data, validateFullForm) => {
      try {
        // Валидация формы
        if (!validateFullForm(data)) {
          return false;
        }

        // Создание инициатора если нужно
        let initiatorId = data.selected_initiator?.value;

        // Проверяем, заполнены ли поля для создания нового инициатора
        const hasNewInitiatorData =
          data.initiator.name &&
          data.initiator.surname &&
          data.initiator.department &&
          data.initiator.phone;

        if (hasNewInitiatorData) {
          const initiatorResponse = await dispatch(
            createInitiatorAction({
              name: data.initiator.name,
              surname: data.initiator.surname,
              department: data.initiator.department,
              phone: data.initiator.phone
            })
          );
          initiatorId = initiatorResponse?.id;
        }

        // Подготовка данных для API
        const filialIds =
          data.cities.includes('all') || data.cities.some((city) => city?.value === 'all')
            ? cities?.filials?.map((filial) => filial.filial_id) || []
            : data.cities
                .filter((city) => city !== 'all' && city?.value !== 'all')
                .map((city) => (typeof city === 'object' ? city.value : city));

        const apiData = {
          created_by_admin_user_id: 1,
          initiator_id: initiatorId,
          start_at: data.date_start?.toISOString(),
          end_at: data.date_end?.toISOString(),
          filial_ids: filialIds,
          is_enabled_for_all_customers: data.availability_type === 'all',
          is_button_enabled: data.show_buttons,
          is_disabled: false,
          buttons: data.show_buttons
            ? data.buttons.map((btn) => ({
                button_id: btn.button_id,
                link: btn.link,
                is_external_link: btn.is_external_link
              }))
            : [],
          file_ids: data.file_ids,
          translations: [
            {
              lang: 'ru',
              promotion_name: data.name_ru,
              short_description: data.short_description_ru,
              description: data.full_description_ru
                ? draftToHtml(convertToRaw(data.full_description_ru.getCurrentContent()))
                : '',
              banner_url:
                typeof data.banner_ru === 'string'
                  ? data.banner_ru
                  : data.banner_ru?.url || data.banner_ru || null
            },
            {
              lang: 'kk',
              promotion_name: data.name_kk,
              short_description: data.short_description_kk,
              description: data.full_description_kk
                ? draftToHtml(convertToRaw(data.full_description_kk.getCurrentContent()))
                : '',
              banner_url:
                typeof data.banner_kk === 'string'
                  ? data.banner_kk
                  : data.banner_kk?.url || data.banner_kk || null
            },
            {
              lang: 'en',
              promotion_name: data.name_en,
              short_description: data.short_description_en,
              description: data.full_description_en
                ? draftToHtml(convertToRaw(data.full_description_en.getCurrentContent()))
                : '',
              banner_url:
                typeof data.banner_en === 'string'
                  ? data.banner_en
                  : data.banner_en?.url || data.banner_en || null
            }
          ]
        };

        // Отправка данных
        if (mode === 'create' || mode === 'copy') {
          await dispatch(createPromotion(apiData));
        } else {
          await dispatch(updatePromotion(promotionId, apiData));
        }

        history.push('/company-promotions');
        return true;
      } catch (error) {
        console.error('Error submitting promotion:', error);
        return false;
      }
    },
    [dispatch, mode, promotionId, history, cities]
  );

  // Функция отмены
  const handleCancel = useCallback(() => {
    history.push('/company-promotions');
  }, [history]);

  // Функция обновления списка кнопок
  const handleButtonsUpdate = useCallback(() => {
    dispatch(getButtonsList());
  }, [dispatch]);

  return {
    // Данные формы
    formData,
    setFormData,
    updateFormData,
    updateNestedFormData,

    // Состояния загрузки
    loading,
    promotionLoading,

    // Ошибки
    error,

    // Данные для селектов
    cities,
    initiators,
    buttons,

    // Функции
    handleSubmit,
    handleCancel,
    handleButtonsUpdate,

    // Флаги
    isInitialized
  };
};
